import React, { useContext, useRef, useState, useEffect } from 'react'
import Helmet from 'react-helmet'
import { useStaticQuery, graphql, navigate } from 'gatsby'
import { GlobalContext } from '../common/global-context'
import withLocation from '../common/with-location'
import { useScrollTop, useIntlLocale } from '../hooks'
import {
  HeaderContainer,
  HeaderWrapper,
  APITypeWrapper,
  HeadNavItem,
  RightDrawer,
  APITypeInnerWrapper,
  UserInfoWrapper,
  HeaderRightPart,
  HeaderIconWrapper,
  LetterAvater,
} from './layout.atoms'
import LogoDoulong from '../images/logo.svg'
import LogoAbroad from '../images/logo-abroad.svg'
import ChinaIco from '../images/favicon.ico'
import ForeignIco from '../images/foreign_doulong.ico'
import Arrow from '../images/arrow.svg'

import { AUTH_KEY, LOGINNAME, isBrowser } from './use-get-info'

const Header = ({ Location, headerNavs, loginName, redirect }) => {
  const { locale, setLocale } = useContext(GlobalContext)
  const t = useIntlLocale()
  const {
    headerNavs: { edges: topNavs },
  } = useStaticQuery(
    graphql`
      query Query {
        headerNavs: allContentfulHeaderDirectory {
          edges {
            node {
              slug
              title
              titleEn
              mainlink {
                slug
              }
              children: contentfulchildren {
                ... on ContentfulDirectories {
                  slug
                  title
                  titleEn
                  children: contentfulchildren {
                    ... on ContentfulPost {
                      slug
                      title
                      titleEn
                      updateTime
                      content {
                        content
                      }
                      contentEn {
                        contentEn
                      }
                    }
                    ... on ContentfulDirectories {
                      slug
                      title
                      titleEn
                      children: contentfulchildren {
                        ... on ContentfulPost {
                          slug
                          title
                          titleEn
                          updateTime
                          content {
                            content
                          }
                          contentEn {
                            contentEn
                          }
                        }
                      }
                    }
                  }
                }
                ... on ContentfulPost {
                  slug
                  title
                  titleEn
                  updateTime
                  content {
                    content
                  }
                  contentEn {
                    contentEn
                  }
                }
              }
            }
          }
        }
      }
    `,
  )
  const [isAbroad, setIsAbroad] = useState(false)

  useEffect(() => {
    if (window) {
      setIsAbroad(
        !['https://cloud-pre.doulong.cn', 'https://console.doulongyun.com'].includes(window.origin),
      )
    }
  }, [])

  const slugs = useRef([])

  const isChinese = process.env.GATSBY_LOCALE === 'zh'
  const getAllSlugs = (slug, children) => {
    if (children) {
      children.forEach((item) => {
        if (item.children?.length > 0) {
          getAllSlugs(`${slug}/${item.slug}`, item.children)
        } else {
          if (isChinese && item.title) {
            slugs.current.push(`/${slug}/${item.slug}`)
          }
          if (!isChinese && item.titleEn) {
            slugs.current.push(`/${slug}/${item.slug}`)
          }
        }
      })
    }
  }

  topNavs
    .filter(({ node }) => (isChinese ? node.title : node.titleEn))
    .forEach(({ node }) => {
      getAllSlugs(node.slug, node.children)
    })

  const { clearScrollTop } = useScrollTop('left-menu')
  const headNavLinkObj = React.useRef({})

  const gpuSlug =
    slugs.current.filter((slug) => slug.startsWith('/gpu-instance/'))[0] ?? '/gpu-instance/api-doc/'
  const cloudGameSlug =
    slugs.current.filter((slug) => slug.startsWith('/streaming-service/'))[0] ??
    '/streaming-service/api-doc/'

  const getNavigation = React.useCallback((obj, slug = '', key) => {
    const s = obj.slug
    if (obj.children) {
      obj.children.forEach((child) => {
        getNavigation(child, `${slug}/${s}`, key)
      })
    } else {
      if (headNavLinkObj.current[key] === s) {
        headNavLinkObj.current[key] = `${slug}/${s}/`
      }
    }
  }, [])

  React.useEffect(() => {
    headerNavs.edges.forEach(({ node }) => {
      headNavLinkObj.current[node.slug] = node.mainlink.slug
      getNavigation(node, '', node.slug)
    })
    if (
      Location.pathname === '/' ||
      Location.pathname === '/document/zh/' ||
      Location.pathname === '/document/en/'
    ) {
      const slugsArr = slugs.current.filter((slug) => slug.startsWith('/gpu-instance/'))
      navigate(slugsArr[0])
      // navigate(headNavLinkObj.current[Object.keys(headNavLinkObj.current)[0]])
    }
  }, [getNavigation, headerNavs.edges, Location.pathname])

  React.useEffect(() => {
    document.body.addEventListener('click', () => {
      setShowLogoutDrawer(false)
    })
  }, [])

  const [showLogoutDrawer, setShowLogoutDrawer] = React.useState(false)

  const logout = (e) => {
    if (isBrowser) {
      window.localStorage.removeItem(AUTH_KEY)
      window.localStorage.removeItem(LOGINNAME)
      redirect()
    }
  }

  const navs = topNavs.map(({ node }) => ({
    ...node,
    to:
      node.slug === 'gpu-instance'
        ? gpuSlug
        : cloudGameSlug
  }))

  return (
    <HeaderContainer>
      <Helmet>
        <title>{t('document.title.0')}</title>
        {isAbroad ? <link rel='icon' href={ForeignIco} /> : <link rel='icon' href={ChinaIco} />}
      </Helmet>
      <HeaderWrapper>
        <div>
          <img
            aria-hidden='true'
            src={isAbroad ? LogoAbroad : LogoDoulong}
            alt='logo png'
            height={24}
            onClick={() => navigate(`${Location.origin}/`)}
          />
          <p style={{ marginLeft: 30 }}>{t('document.header.0')}</p>
        </div>
        <HeaderRightPart>
          <HeaderIconWrapper
            onClick={() => {
              setLocale(locale === 'en' ? 'zh' : 'en')
              window.location.href = `${window.location.origin}/document/${
                locale === 'en' ? 'zh' : 'en'
              }/`
            }}
          >
            {locale === 'en' ? 'EN' : '中'}
          </HeaderIconWrapper>
          <UserInfoWrapper
            aria-hidden='true'
            onClick={(e) => {
              e.stopPropagation()
              setShowLogoutDrawer(s => !s)
            }}
            show={showLogoutDrawer}
          >
            <div>
              <LetterAvater>{loginName?.slice(0, 1)}</LetterAvater>
              {loginName}
              <img src={Arrow} alt='arrow' />
            </div>
            <RightDrawer show={showLogoutDrawer}>
              <p aria-hidden='true' onClick={logout}>
                {t('document.header.2')}
              </p>
            </RightDrawer>
          </UserInfoWrapper>
        </HeaderRightPart>
      </HeaderWrapper>
      <APITypeWrapper>
        <APITypeInnerWrapper>
          {navs
            .filter((node) => (process.env.GATSBY_LOCALE === 'zh' ? node.title : node.titleEn))
            .map((node) => (
              <HeadNavItem
                key={node.slug}
                to={node.to}
                active={Location.pathname.includes(`${node.slug}`) ? 1 : 0}
                onClick={clearScrollTop}
              >
                {process.env.GATSBY_LOCALE === 'zh' ? node.title : node.titleEn}
              </HeadNavItem>
            ))}
        </APITypeInnerWrapper>
      </APITypeWrapper>
    </HeaderContainer>
  )
}

export default withLocation(Header)
